/**
 * Created: 2026-08-29
 * Initial commit: Extract slot grid merging logic into shared/ (feat/infra/mr-18)
 *
 * Framework-agnostic slot grid merging logic, shared between the web admin
 * app and (eventually) the WeChat mini-program. Groups adjacent grid cells
 * that share the same order, event, coaching appointment, or reservation
 * reason into rectangular spans for rendering.
 */

export interface SlotSpanCourt {
  id: string
}

export interface SlotSpanMeta {
  orderId?: string
  eventId?: string
  reason?: string
  /** Hours of one coaching appointment share this and merge like an order. */
  sessionId?: string
}

export interface SpanBlock<T extends SlotSpanMeta> {
  /** 0-based index into the courts array */
  courtIndex: number
  hourStart: number
  rowSpan: number
  colSpan: number
  /** representative slot (top-left cell) */
  meta: T
}

export interface SpanBlocksResult<T extends SlotSpanMeta> {
  spanBlocks: SpanBlock<T>[]
  /** Cells covered by a span block — rendered by the span layer instead */
  coveredCells: Set<string>
  /** cellKey ("hour-courtId") -> spanKey, for looking up which span a cell belongs to */
  spanKeyMap: Map<string, string>
  /** spanKey -> the list of cells it covers */
  spanGroupCells: Map<string, { hour: number; courtId: string }[]>
}

/**
 * Compute span blocks (merged cells), covered cells, and span maps for a slot
 * grid. Merges both vertically (consecutive hours, same court) and
 * horizontally (adjacent courts) when cells share the same orderId, eventId,
 * sessionId, or reason.
 */
export function computeSpanBlocks<T extends SlotSpanMeta>(
  courts: SlotSpanCourt[],
  hours: number[],
  slotLookup: Map<string, T>,
): SpanBlocksResult<T> {
  const blocks: SpanBlock<T>[] = []
  const covered = new Set<string>()
  const keyMap = new Map<string, string>()
  const grpCells = new Map<string, { hour: number; courtId: string }[]>()

  // Phase 1: Group cells by orderId / eventId → bounding rectangle span
  const idBuckets = new Map<
    string,
    { courtIndex: number; hour: number; courtId: string }[]
  >()
  for (const [ci, court] of courts.entries()) {
    for (const hour of hours) {
      const meta = slotLookup.get(`${hour}-${court.id}`)
      if (!meta) continue
      const id = meta.orderId
        ? `order:${meta.orderId}`
        : meta.eventId
          ? `event:${meta.eventId}`
          : meta.sessionId
            ? `coaching:${meta.sessionId}`
            : null
      if (!id) continue
      if (!idBuckets.has(id)) idBuckets.set(id, [])
      idBuckets.get(id)?.push({ courtIndex: ci, hour, courtId: court.id })
    }
  }

  let p1RunCounter = 0
  for (const [id, cells] of idBuckets) {
    if (cells.length === 0) continue

    // Sort unique court indices to find contiguous runs — a gap means two courts
    // that share an orderId/eventId but are not adjacent (e.g. court 1 and court 6)
    // must NOT be merged into a single span that would wrongly cover the courts in
    // between. Each contiguous run becomes its own span block (or is left as an
    // individual cell when rowSpan=1, colSpan=1).
    const uniqueCIs = [...new Set(cells.map((c) => c.courtIndex))].sort((a, b) => a - b)

    let i = 0
    while (i < uniqueCIs.length) {
      // Extend the run as long as the next court index is immediately adjacent
      let j = i
      while (j + 1 < uniqueCIs.length && uniqueCIs[j + 1] === uniqueCIs[j] + 1) j++

      const minCI = uniqueCIs[i]
      const colSpan = j - i + 1
      const ciSet = new Set(uniqueCIs.slice(i, j + 1))

      const runCells = cells.filter((c) => ciSet.has(c.courtIndex))

      // For a multi-court span the hour sets must be identical across every court
      // in the run, otherwise the cells don't form a valid rectangle.
      const hoursByCourt = new Map<number, Set<number>>()
      for (const c of runCells) {
        if (!hoursByCourt.has(c.courtIndex)) hoursByCourt.set(c.courtIndex, new Set())
        hoursByCourt.get(c.courtIndex)?.add(c.hour)
      }
      const hourSets = [...hoursByCourt.values()]
      const referenceHours = hourSets[0]
      const hoursMatch =
        hourSets.length === 1 ||
        hourSets.every(
          (s) =>
            s.size === referenceHours.size &&
            [...s].every((h) => referenceHours.has(h)),
        )

      if (!hoursMatch) {
        i = j + 1
        continue
      }

      const runHours = runCells.map((c) => c.hour)
      const minH = Math.min(...runHours)
      const maxH = Math.max(...runHours)
      const rowSpan = maxH - minH + 1

      // Verify every hour in [minH, maxH] is present for every court in the run —
      // a gap would cause the span to swallow cells belonging to other orders.
      const allHoursPresent = (() => {
        for (const [, hourSet] of hoursByCourt) {
          for (let h = minH; h <= maxH; h++) {
            if (!hourSet.has(h)) return false
          }
        }
        return true
      })()
      if (!allHoursPresent) {
        i = j + 1
        continue
      }

      if (rowSpan > 1 || colSpan > 1) {
        const spanKey = `${id}-${++p1RunCounter}`
        const cellList = runCells.map((c) => ({ hour: c.hour, courtId: c.courtId }))
        for (const c of cellList) {
          keyMap.set(`${c.hour}-${c.courtId}`, spanKey)
          covered.add(`${c.hour}-${c.courtId}`)
        }
        grpCells.set(spanKey, cellList)
        const meta = slotLookup.get(`${minH}-${courts[minCI].id}`)
        if (meta)
          blocks.push({ courtIndex: minCI, hourStart: minH, rowSpan, colSpan, meta })
      }

      i = j + 1
    }
  }

  // Phase 2: Reason-only cells — build vertical runs per court, then merge adjacent courts
  // whose runs have identical (hourStart, hourEnd, reason, orderId, eventId).
  const reasonRuns: {
    ci: number
    courtId: string
    hourStart: number
    hourEnd: number
    reason: string
    orderId: string | undefined
    eventId: string | undefined
  }[] = []

  for (const [ci, court] of courts.entries()) {
    let runStart: number | null = null
    let runReason: string | undefined
    let runOrderId: string | undefined
    let runEventId: string | undefined

    for (const hour of hours) {
      const cellKey = `${hour}-${court.id}`
      // Coaching cells merge only by sessionId (Phase 1), never by reason,
      // so they break a reason run like an already-covered cell does.
      if (covered.has(cellKey) || slotLookup.get(cellKey)?.sessionId) {
        if (runStart !== null && runReason !== undefined) {
          reasonRuns.push({
            ci,
            courtId: court.id,
            hourStart: runStart,
            hourEnd: hour - 1,
            reason: runReason,
            orderId: runOrderId,
            eventId: runEventId,
          })
          runStart = null
          runReason = undefined
          runOrderId = undefined
          runEventId = undefined
        }
        continue
      }
      const meta = slotLookup.get(cellKey)
      const reason = meta?.reason
      const orderId = meta?.orderId
      const eventId = meta?.eventId
      if (
        reason !== undefined &&
        reason === runReason &&
        orderId === runOrderId &&
        eventId === runEventId
      ) {
        // extend existing run
      } else {
        if (runStart !== null && runReason !== undefined) {
          reasonRuns.push({
            ci,
            courtId: court.id,
            hourStart: runStart,
            hourEnd: hour - 1,
            reason: runReason,
            orderId: runOrderId,
            eventId: runEventId,
          })
        }
        runStart = reason !== undefined ? hour : null
        runReason = reason
        runOrderId = orderId
        runEventId = eventId
      }
    }
    if (runStart !== null && runReason !== undefined) {
      reasonRuns.push({
        ci,
        courtId: court.id,
        hourStart: runStart,
        hourEnd: hours[hours.length - 1],
        reason: runReason,
        orderId: runOrderId,
        eventId: runEventId,
      })
    }
  }

  // Group runs by (hourStart, hourEnd, reason), then find contiguous court sequences
  const runBuckets = new Map<string, typeof reasonRuns>()
  for (const run of reasonRuns) {
    const k = `${run.hourStart}:${run.hourEnd}:${run.reason}:${run.orderId ?? ''}:${run.eventId ?? ''}`
    if (!runBuckets.has(k)) runBuckets.set(k, [])
    runBuckets.get(k)?.push(run)
  }

  let rgc = 0
  for (const [, runs] of runBuckets) {
    const sorted = [...runs].sort((a, b) => a.ci - b.ci)
    let i = 0
    while (i < sorted.length) {
      let j = i
      while (j + 1 < sorted.length && sorted[j + 1].ci === sorted[j].ci + 1) j++
      const sub = sorted.slice(i, j + 1)
      const colSpan = sub.length
      const { ci: minCI, hourStart, hourEnd } = sub[0]
      const rowSpan = hourEnd - hourStart + 1

      const spanKey = `rg-${++rgc}`
      const cellList: { hour: number; courtId: string }[] = []

      for (const run of sub) {
        for (let h = run.hourStart; h <= run.hourEnd; h++) {
          const cellKey = `${h}-${run.courtId}`
          keyMap.set(cellKey, spanKey)
          cellList.push({ hour: h, courtId: run.courtId })
          if (rowSpan > 1 || colSpan > 1) covered.add(cellKey)
        }
      }

      if (rowSpan > 1 || colSpan > 1) {
        const meta = slotLookup.get(`${hourStart}-${courts[minCI].id}`)
        if (meta) blocks.push({ courtIndex: minCI, hourStart, rowSpan, colSpan, meta })
        grpCells.set(spanKey, cellList)
      }

      i = j + 1
    }
  }

  return {
    spanBlocks: blocks,
    coveredCells: covered,
    spanKeyMap: keyMap,
    spanGroupCells: grpCells,
  }
}
